/* eslint-disable max-lines-per-function */
import { compact, isEmpty, sumBy } from 'lodash';
import { useCallback, useMemo } from 'react';

import {
   createDynamicColumnsWidth,
   HorizontalAlign,
   InteractiveTable,
   InteractiveTableColumnConfiguration,
   InteractiveTableValidation,
   JustifyContent,
   Padding,
} from '@dreryk/react-components';

import { visitDetailsTranslationKeys } from '../../../../../translations';
import { DiagnosisTableBean } from '../DiagnosesFormTab.types';

import { ChronicDiseaseCell } from './cells/ChronicDiseaseCell';
import { DiagnosisAdditionalInformationCell } from './cells/DiagnosisAdditionalInformationCell';
import { MainCell } from './cells/MainCell';
import { NameCell } from './cells/NameCell';
import { RemoveCell } from './cells/RemoveCell';

export const useDiagnosesInteractiveTableValidation = (
   required: boolean
): InteractiveTableValidation<DiagnosisTableBean> => {
   return useCallback(
      (diagnoses) => {
         if (isEmpty(diagnoses)) {
            return required ? visitDetailsTranslationKeys.diagnoses.chooseDiagnosis : true;
         }

         const mainCount = sumBy(diagnoses, (diagnosis) => Number(diagnosis.main));
         if (mainCount < 1) {
            return visitDetailsTranslationKeys.diagnoses.noMainDiagnosis;
         }

         if (mainCount > 1) {
            return visitDetailsTranslationKeys.diagnoses.tooManyMainDiagnoses;
         }

         return true;
      },
      [required]
   );
};

export const useDiagnosesInteractiveTableColumnsConfiguration = (
   chronicEditable: boolean,
   visitId: number
): InteractiveTableColumnConfiguration<DiagnosisTableBean>[] => {
   return useMemo(() => {
      const columnWidths = createDynamicColumnsWidth({
         code: '76px',
         name: '100%',
         main: '70px',
         chronic: '89px',
         diagnosisAdditionalInformation: '24px',
         remove: '24px',
      });

      return compact([
         {
            name: 'code',
            label: visitDetailsTranslationKeys.diagnoses.icd10,
            width: columnWidths.code,

            renderer: ({ bean, index }) => {
               return (
                  <InteractiveTable.TextViewCell testId={'code'} name={'code'} index={index} defaultValue={bean.code} />
               );
            },
         },
         {
            name: 'name',

            renderer: ({ bean: { additionalInformation, name }, index }) => (
               <NameCell additionalInformation={additionalInformation} name={name} index={index} />
            ),
            label: visitDetailsTranslationKeys.diagnoses.diseaseName,
            width: columnWidths.name,
         },
         {
            name: 'main',
            label: visitDetailsTranslationKeys.diagnoses.main,
            width: columnWidths.main,
            justifyContent: JustifyContent.CENTER,
            textAlign: HorizontalAlign.CENTER,

            renderer: ({ bean, index }): JSX.Element => {
               return (
                  <MainCell testId={'main'} name={'main'} index={index} defaultValue={bean.main} visitId={visitId} />
               );
            },
         },
         {
            name: 'chronic',
            label: visitDetailsTranslationKeys.diagnoses.chronic,
            width: columnWidths.chronic,
            justifyContent: JustifyContent.CENTER,
            textAlign: HorizontalAlign.CENTER,

            renderer: ({ bean, index }): JSX.Element => {
               return (
                  <ChronicDiseaseCell
                     testId={'chronic'}
                     name={'chronic'}
                     index={index}
                     defaultValue={bean.chronic}
                     visitId={visitId}
                     chronicEditable={chronicEditable}
                  />
               );
            },
         },
         {
            name: 'diagnosisAdditionalInformation',
            width: columnWidths.diagnosisAdditionalInformation,

            renderer: ({ bean: { code, name }, index }) => (
               <DiagnosisAdditionalInformationCell code={code} name={name} visitId={visitId} index={index} />
            ),
         },
         {
            name: 'remove',
            width: columnWidths.remove,
            padding: Padding.NO_RIGHT,

            renderer: ({ index }) => {
               return <RemoveCell visitId={visitId} index={index} />;
            },
         },
      ]);
   }, [chronicEditable, visitId]);
};
